import type { SvgIconComponent } from "@mui/icons-material";
import CalendarIcon from "@mui/icons-material/CalendarTodayRounded";
import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import { Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from "@mui/material";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import UserAvatar from "@entrilia/shared/components/UserAvatar";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import type { WorkflowActivity } from "../../../../../api/types/workflowTypes";
import NoteIcon from "@entrilia/shared/icons/NoteIcon";
import SparklesIcon from "@entrilia/shared/icons/SparklesIcon";
import { getActivityDisplayStatus } from "./workflowDetailsHelpers";

interface Props {
  activity: WorkflowActivity;
}

interface Row {
  Icon: SvgIconComponent;
  label: string;
  value: React.ReactNode;
}

const WorkflowActivityDetailInfoSection = ({ activity }: Props) => {
  if (
    !activity.executedByUserName &&
    !activity.finishedAt &&
    !activity.onBehalfOfUserName &&
    !activity.statusChangeComment
  ) {
    return null;
  }

  const displayStatus = getActivityDisplayStatus(activity);

  const rows: Row[] = [];

  if (activity.executedByUserName) {
    rows.push({
      Icon: PersonIcon,
      label: displayStatus === "Skipped" ? "Skipped by" : "Executed by",
      value: (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          {activity.executedByUserName === "AI Agent" ? (
            <SparklesIcon color="primary" sx={{ width: 24, height: 24 }} />
          ) : (
            <UserAvatar size="small" userName={activity.executedByUserName} />
          )}
          <Typography>{activity.executedByUserName}</Typography>
        </Stack>
      ),
    });
  }

  if (activity.finishedAt) {
    rows.push({
      Icon: CalendarIcon,
      label: displayStatus === "Skipped" ? "Skipped at" : "Executed at",
      value: <Typography>{convertISODateTime(activity.finishedAt)}</Typography>,
    });
  }

  if (activity.onBehalfOfUserName) {
    rows.push({
      Icon: PersonIcon,
      label: "On behalf of",
      value: (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <UserAvatar size="small" userName={activity.onBehalfOfUserName} />
          <Typography>{activity.onBehalfOfUserName}</Typography>
        </Stack>
      ),
    });
  }

  if (activity.statusChangeComment) {
    rows.push({
      Icon: NoteIcon,
      label: "Comment",
      value: <TypographyTooltipEllipsis text={activity.statusChangeComment} />,
    });
  }

  return (
    <TableContainer>
      <Table sx={{ width: "auto" }}>
        <TableBody>
          {rows.map(({ Icon, label, value }) => (
            <TableRow key={label} sx={{ "& td": { border: 0 } }}>
              <TableCell sx={{ verticalAlign: "middle", pl: 0, py: 1.25 }}>
                <Stack spacing={1.5} direction="row" sx={{ alignItems: "center" }}>
                  <Icon color="action" />
                  <Typography color="textSecondary">{label}</Typography>
                </Stack>
              </TableCell>
              <TableCell sx={{ verticalAlign: "middle", py: 1.25, maxWidth: "68rem" }}>{value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default WorkflowActivityDetailInfoSection;
